import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from '@/shared/context/TeamContext'
import { cemcApi } from '../api/cemc.api'
import type { ConfiguracionCEMC, FuenteDatosCEMC, CursoCEMC } from '../types'

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

// ─── Navegación dinámica del sidebar ────────────────────────────────────────────

export function useCemcEducacionNav() {
  const { selectedArea } = useTeam()
  const { data, isLoading } = useQuery({
    queryKey: ['cemc-mis-configuraciones', selectedArea?.id],
    queryFn: () => cemcApi.getMisConfiguraciones(),
  })

  const grupos = useMemo(() => {
    if (!data) return []
    return data
      .map((c) => ({ id: c.config_id, name: c.grupo_nombre, slug: toSlug(c.grupo_nombre) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  return { grupos, isLoading }
}

// ─── Configuraciones ──────────────────────────────────────────────────────────

export function useConfiguracionesCEMC(params?: { page?: number; search?: string }) {
  return useQuery({
    queryKey: ['cemc-configuraciones', params],
    queryFn: () => cemcApi.getConfiguraciones(params),
  })
}

export function useConfiguracionCEMC(id: number) {
  return useQuery({
    queryKey: ['cemc-configuracion', id],
    queryFn: () => cemcApi.getConfiguracion(id),
    enabled: !!id,
  })
}

export function useCreateConfiguracionCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<ConfiguracionCEMC>) => cemcApi.createConfiguracion(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-configuraciones'] }),
  })
}

export function useUpdateConfiguracionCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<ConfiguracionCEMC> }) =>
      cemcApi.updateConfiguracion(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cemc-configuraciones'] })
      qc.invalidateQueries({ queryKey: ['cemc-configuracion'] })
    },
  })
}

export function useDeleteConfiguracionCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cemcApi.deleteConfiguracion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-configuraciones'] }),
  })
}

export function useToggleActivoCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cemcApi.toggleActivo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-configuraciones'] }),
  })
}

// ─── Fuentes ──────────────────────────────────────────────────────────────────

export function useFuentesCEMC(configId: number) {
  return useQuery({
    queryKey: ['cemc-fuentes', configId],
    queryFn: () => cemcApi.getFuentes(configId),
    enabled: !!configId,
  })
}

export function useCreateFuenteCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, payload }: { configId: number; payload: Partial<FuenteDatosCEMC> }) =>
      cemcApi.createFuente(configId, payload),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['cemc-fuentes', vars.configId] }),
  })
}

export function useUpdateFuenteCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<FuenteDatosCEMC> }) =>
      cemcApi.updateFuente(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-fuentes'] }),
  })
}

export function useDeleteFuenteCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cemcApi.deleteFuente(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-fuentes'] }),
  })
}

// ─── Cursos ───────────────────────────────────────────────────────────────────

export function useCursosCEMC(configId: number) {
  return useQuery({
    queryKey: ['cemc-cursos', configId],
    queryFn: () => cemcApi.getCursos(configId),
    enabled: !!configId,
  })
}

export function useCreateCursoCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, payload }: { configId: number; payload: Partial<CursoCEMC> }) =>
      cemcApi.createCurso(configId, payload),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['cemc-cursos', vars.configId] }),
  })
}

export function useUpdateCursoCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<CursoCEMC> }) =>
      cemcApi.updateCurso(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-cursos'] }),
  })
}

export function useDeleteCursoCEMC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cemcApi.deleteCurso(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cemc-cursos'] }),
  })
}

// ─── Usuarios por módulo ──────────────────────────────────────────────────────

export function useUsuariosModulo(
  configId: number,
  cursoId: number,
  moduloNum: number,
  params?: { page?: number; page_size?: number; search?: string; sort_field?: string; sort_dir?: string }
) {
  return useQuery({
    queryKey: ['cemc-usuarios-modulo', configId, cursoId, moduloNum, params],
    queryFn: () => cemcApi.getUsuariosModulo(configId, cursoId, moduloNum, params),
    enabled: !!configId && !!cursoId && !!moduloNum,
  })
}

// ─── Estadísticas ─────────────────────────────────────────────────────────────

export function useEstadisticasCEMC(configId: number) {
  return useQuery({
    queryKey: ['cemc-estadisticas', configId],
    queryFn: () => cemcApi.getEstadisticas(configId),
    enabled: !!configId,
  })
}
